import { Box, Group, Text, Badge, Stack } from '@mantine/core';
import type { CohortHistoryEntry } from '../../../types';
import { format, isSameYear } from 'date-fns';
import { parseDateOnly } from '../../../utils/dateHelpers';
import {
  ATTENDANCE_MODALITIES,
  modalityAttendancePrefix,
} from '../../../utils/attendanceModalityLabels';

// Active cohort statuses that should be highlighted
const ACTIVE_STATUSES = ['live', 'ready_to_launch', 'in_prep'];

interface CohortHistoryCardProps {
  entry: CohortHistoryEntry;
  onClick?: () => void;
}

/**
 * Format date range for display
 * Same year: "Jan 14 – Apr 14"
 * Different years: "Jun 2024 – Aug 2024"
 */
function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) return '';

  const now = new Date();
  // MED-1265: DATE columns arrive as UTC-midnight timestamps; parse as calendar dates
  const start = startDate ? parseDateOnly(startDate) : null;
  const end = endDate ? parseDateOnly(endDate) : null;

  // Check if dates are in the current year
  const startInCurrentYear = start ? isSameYear(start, now) : false;
  const endInCurrentYear = end ? isSameYear(end, now) : false;
  const bothInCurrentYear = startInCurrentYear && endInCurrentYear;

  if (start && end) {
    if (bothInCurrentYear) {
      // Same year (current): "Jan 14 – Apr 14"
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
    } else {
      // Different years or past year: "Jun 2024 – Aug 2024"
      return `${format(start, 'MMM yyyy')} – ${format(end, 'MMM yyyy')}`;
    }
  } else if (start) {
    return bothInCurrentYear ? format(start, 'MMM d') : format(start, 'MMM yyyy');
  } else if (end) {
    return bothInCurrentYear ? format(end, 'MMM d') : format(end, 'MMM yyyy');
  }

  return '';
}

/**
 * CohortHistoryCard - Displays a cohort in the participant's history
 *
 * Design specs:
 * - Current/active cohorts: orange border
 * - Completed cohorts: standard border
 * - Code badge (gray background)
 * - CURRENT label for active cohorts
 * - Status badge: LIVE (orange filled) or COMPLETED (green outlined)
 * - Attendance percentage for completed cohorts
 */
export function CohortHistoryCard({ entry, onClick }: CohortHistoryCardProps) {
  const isActive = ACTIVE_STATUSES.includes(entry.cohort_status);
  const isCompleted = entry.cohort_status === 'completed';
  // MED-1442: one line per bucket with a rate, so a 1:1-only or workshop-only
  // cohort shows its attendance too.
  const attendanceLines = isCompleted
    ? ATTENDANCE_MODALITIES.flatMap((k) => {
        const pct = entry.attendance_by_modality?.[k]?.percentage;
        return pct === null || pct === undefined ? [] : [{ k, pct }];
      })
    : [];

  // Get status badge config
  const getStatusBadge = () => {
    if (entry.cohort_status === 'live') {
      return { label: 'LIVE', color: 'statusLive', variant: 'filled' as const };
    }
    if (entry.cohort_status === 'completed') {
      return { label: 'COMPLETED', color: 'medleyGreen', variant: 'light' as const };
    }
    // For other statuses like in_prep, ready_to_launch, etc.
    return { label: entry.cohort_status.toUpperCase().replace('_', ' '), color: 'gray', variant: 'light' as const };
  };

  const statusBadge = getStatusBadge();
  const dateRange = formatDateRange(entry.start_date, entry.end_date);

  return (
    <Box
      onClick={onClick}
      style={{
        padding: '16px',
        borderRadius: '8px',
        border: isActive ? '2px solid var(--mantine-color-warningAmber-5)' : '1px solid var(--mantine-color-gray-3)',
        backgroundColor: 'white',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Group justify="space-between" align="flex-start">
        {/* Left side: Code badge, CURRENT label, name, dates */}
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="xs">
            {/* Cohort code badge */}
            {entry.cohort_code && (
              <Badge
                variant="filled"
                color="gray"
                size="sm"
                styles={{
                  root: {
                    backgroundColor: 'var(--mantine-color-gray-2)',
                    color: 'var(--mantine-color-gray-7)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  },
                }}
              >
                {entry.cohort_code}
              </Badge>
            )}

            {/* CURRENT label for active cohorts */}
            {isActive && (
              <Text size="xs" fw={600} c="warningAmber" style={{ letterSpacing: '0.3px' }}>
                CURRENT
              </Text>
            )}
          </Group>

          {/* Cohort name */}
          <Text fw={600} size="sm" lineClamp={1}>
            {entry.cohort_name}
          </Text>

          {/* Date range */}
          {dateRange && (
            <Text size="xs" c="dimmed">
              {dateRange}
            </Text>
          )}
        </Stack>

        {/* Right side: Status badge and attendance */}
        <Stack gap={4} align="flex-end">
          <Badge
            variant={statusBadge.variant}
            color={statusBadge.color}
            size="sm"
            styles={{
              root: {
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
              },
            }}
          >
            {statusBadge.label}
          </Badge>

          {/* Attendance percentage for completed cohorts */}
          {attendanceLines.map(({ k, pct }) => (
            <Text key={k} size="xs" c="medleyGreen" fw={500}>
              {pct}% {modalityAttendancePrefix(k, entry.group_coaching_sub_type)} attended
            </Text>
          ))}
        </Stack>
      </Group>
    </Box>
  );
}

export default CohortHistoryCard;
